import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { styled } from '@mui/system'
import Grid from '@mui/material/Grid2'
import Button from '@mui/material/Button'

import AuthContext from '@cpg-its/ads.auth.auth-context'
import SessionTimeout from '@cpg-its/ads.auth.session-timeout'

import { getUserData } from '../../actions/userActions'

const AccessTokenWrapper = styled(Grid)({
  wordBreak: 'break-all',
})

const AttributeWrapper = styled(Grid)({
  marginBottom: '1rem',
})

const Label = styled(Grid)({
  fontWeight: 600,
  marginRight: '.5rem',
})

const OneIndentation = styled(Grid)({
  marginLeft: '1rem',
})

const AuthView = ({
  auth0LogoutUrl,
  cpgLogoutUrl,
}) => {
  const [timeout, setTimeout] = useState(3000)
  const [countdown, setCountdown] = useState(120)
  const [throttle, setThrottle] = useState(5)
  const [cacheMode, setCacheMode] = useState('on')
  const [userData, setUserData] = useState({})

  const logoutUrl = `${cpgLogoutUrl}?returnTo=${auth0LogoutUrl}?returnTo=${window.location.origin}`

  const { accessToken, decodedToken, getAccessToken, tokenError } = useContext(AuthContext)

  useEffect(() => {
    if (tokenError === 'Unknown or invalid refresh token.' || tokenError === 'Consent required') {
      window.location.replace(logoutUrl)
    }
  }, [tokenError])

  const clientNumber = (() => {
    console.log("Decoded Token", decodedToken)
    if (decodedToken) {
      if (Object.prototype.hasOwnProperty.call(decodedToken, 'sub')) {
        return decodedToken.sub.split('|').pop()
      }
    }
    return decodedToken
  })()

  const renderFullToken = (
    <>
      <h3 className="header">
        Access Token:
      </h3>
      <AccessTokenWrapper>
        {accessToken}
      </AccessTokenWrapper>
    </>
  )

  const renderAud = () => (
    <AttributeWrapper>
      <Label>
        aud:
      </Label>
      {decodedToken.aud.map((aud) => (
        <Grid key={aud}>
          {aud}
        </Grid>
      ))}
    </AttributeWrapper>
  )

  const renderAzp = () => (
    <AttributeWrapper>
      <Label>
        azp:
      </Label>
      {decodedToken.azp}
    </AttributeWrapper>
  )

  const renderExp = () => (
    <AttributeWrapper>
      <Label>
        exp:
      </Label>
      {decodedToken.exp}
    </AttributeWrapper>
  )

  const renderOidc = () => {
    const parsed = JSON.parse(decodedToken['https://oidc.cpg.org/profile'])
    console.log(parsed)
    return (
      <AttributeWrapper>
        <Label>
          https://oidc.cpg.org/profile:
        </Label>
        <Grid>
          {Object.keys(parsed).map((key) => (
            <React.Fragment key={key}>
              <Grid>
                {key}
                :
              </Grid>
              <OneIndentation>
                {Object.keys(parsed[key]).map((childOne) => {
                  if (typeof parsed[key][childOne] !== 'object') {
                    return (
                      <Grid key={childOne}>
                        <span
                          style={{
                            marginRight: '.5rem',
                          }}
                        >
                          {childOne}
                          :
                        </span>
                        {parsed[key][childOne]}
                      </Grid>
                    )
                  }
                  return (
                    <React.Fragment key={childOne}>
                      <Grid>
                        {childOne}
                        :
                      </Grid>
                      <OneIndentation>
                        {Object.keys(parsed[key][childOne]).map((childTwo) => (
                          <React.Fragment key={childTwo}>
                            <span
                              style={{
                                marginRight: '.5rem',
                              }}
                            >
                              {childTwo}
                              :
                            </span>
                            {parsed[key][childOne][childTwo].join(', ')}
                          </React.Fragment>
                        ))}
                      </OneIndentation>
                    </React.Fragment>
                  )
                })}
              </OneIndentation>
            </React.Fragment>
          ))}
        </Grid>
      </AttributeWrapper>
    )
  }

  const renderIat = () => (
    <AttributeWrapper>
      <Label>
        iat:
      </Label>
      {decodedToken.iat}
    </AttributeWrapper>
  )

  const renderIss = () => (
    <AttributeWrapper>
      <Label>
        iss:
      </Label>
      {decodedToken.iss}
    </AttributeWrapper>
  )

  const renderScope = () => (
    <AttributeWrapper>
      <Label>
        scope:
      </Label>
      {decodedToken.scope}
    </AttributeWrapper>
  )

  const renderSub = () => (
    <AttributeWrapper>
      <Label>
        sub:
      </Label>
      {decodedToken.sub}
    </AttributeWrapper>
  )

  const renderClientNumber = () => (
    <>
      <h3 className="header">
        Client Number:
      </h3>
      <AttributeWrapper>
        {clientNumber}
      </AttributeWrapper>
    </>
  )

  const renderUserData = (() => {
    const status = Object.prototype.hasOwnProperty.call(userData, 'status') ? userData.status : null
    const data = Object.prototype.hasOwnProperty.call(userData, 'data') ? userData.data : null

    if (status && status.code === 'SUCCESS') {
      if (data) {
        return (
          <Grid>
            {Object.keys(data).map((key) => (
              <Grid container key={key}>
                <Grid
                  style={{
                    fontWeight: 500,
                    marginRight: '1rem',
                    marginBottom: '1rem',
                  }}
                >
                  {`${key}:`}
                </Grid>
                <Grid>
                  {data[key]}
                </Grid>
              </Grid>
            ))}
          </Grid>
        )
      }
    }
    return null
  })()

  return (
    <>
      <Button onClick={() => window.location.replace(logoutUrl)}>
        Log out
      </Button>
      <hr />
      <Grid>
        <Button
          onClick={() => getAccessToken('off')}
        >
          Force Token Refresh
        </Button>
      </Grid>
      <Grid>
        <Grid>
          Set Session Timeout in Seconds
          <input
            type="number"
            placeholder={timeout}
            onChange={(e) => setTimeout(parseInt(e.target.value, 10) || 1080)}
          />
        </Grid>
        <Grid>
          Set Countdown in Seconds
          <input
            type="number"
            placeholder={countdown}
            onChange={(e) => setCountdown(parseInt(e.target.value, 10) || 120)}
          />
        </Grid>
        <Grid>
          Set Throttle in Seconds
          <input
            type="number"
            placeholder={throttle}
            onChange={(e) => setThrottle(parseInt(e.target.value, 10) || 300)}
          />
        </Grid>
        <Grid>
          Cache Mode (When checked, Access Token refreshes but does not change)
          <input
            type="checkbox"
            checked={cacheMode === 'on'}
            onChange={() => setCacheMode(cacheMode === 'on' ? 'off' : 'on')}
          />
        </Grid>
      </Grid>
      {renderFullToken}
      <br />
      {decodedToken && (
        <>
          <h3 className="header">
            Decoded JWT:
          </h3>
          {renderAud()}
          {renderAzp()}
          {renderExp()}
          {renderOidc()}
          {renderIat()}
          {renderIss()}
          {renderScope()}
          {renderSub()}
          <br />
          {renderClientNumber()}
        </>
      )}
      <hr />
      <Button
        onClick={async() => {
          setUserData(await getUserData(accessToken, clientNumber))
        }}
      >
        Fetch User Data
      </Button>
      {renderUserData}

      <SessionTimeout
        countdown={countdown}
        onActivity={() => {
          console.log('get access token')
          getAccessToken(cacheMode)
        }}
        timeout={timeout}
        logoutUrl={logoutUrl}
        throttle={throttle}
      />
    </>
  )
}

AuthView.propTypes = {
  auth0LogoutUrl: PropTypes.string,
  cpgLogoutUrl: PropTypes.string,
}

export default AuthView
